import { getSprintBoardAuthClient } from './sprintBoardAdminAuth';

export interface SprintBoardConfig {
  id: string;
  project_name: string;
  last_updated: string;
}

export interface SprintBoardEpicRow {
  id: string;
  title: string;
  status: string;
}

export interface SprintBoardStoryRow {
  id: string;
  epic_id: string | null;
  title: string;
  assignee: string;
  status: string;
  issue_type?: string;
  user_story: string | null;
  acceptance_criteria: string[] | null;
  test_cases: string[] | null;
}

export interface SprintData {
  projectName: string;
  lastUpdated: string;
  epics: { id: string; title: string; status: string }[];
  stories: {
    id: string;
    epicId: string | null;
    title: string;
    assignee: string;
    status: string;
    issueType: 'story' | 'bug';
    userStory?: string;
    acceptanceCriteria?: string[];
    testCases?: string[];
  }[];
}

function rowToEpic(row: SprintBoardEpicRow): { id: string; title: string; status: string } {
  return { id: row.id, title: row.title, status: row.status };
}

function rowToStory(row: SprintBoardStoryRow): SprintData['stories'][0] {
  const issueType = row.issue_type === 'bug' ? 'bug' : 'story';
  return {
    id: row.id,
    epicId: row.epic_id ?? null,
    title: row.title,
    assignee: row.assignee ?? '',
    status: row.status,
    issueType,
    userStory: row.user_story ?? undefined,
    acceptanceCriteria: Array.isArray(row.acceptance_criteria) ? row.acceptance_criteria : undefined,
    testCases: Array.isArray(row.test_cases) ? row.test_cases : undefined,
  };
}

export async function fetchSprintData(): Promise<SprintData | null> {
  const client = getSprintBoardAuthClient();
  const [configRes, epicsRes, storiesRes] = await Promise.all([
    client.from('sprint_board_config').select('*').maybeSingle(),
    client.from('sprint_board_epics').select('*').order('id'),
    client.from('sprint_board_stories').select('*').order('id'),
  ]);

  if (configRes.error || epicsRes.error || storiesRes.error) {
    console.warn('Sprint board fetch error:', configRes.error || epicsRes.error || storiesRes.error);
    return null;
  }

  const config = configRes.data as SprintBoardConfig | null;
  const epics = (epicsRes.data as SprintBoardEpicRow[]) ?? [];
  const stories = (storiesRes.data as SprintBoardStoryRow[]) ?? [];

  return {
    projectName: config?.project_name ?? 'Bravos',
    lastUpdated: config?.last_updated ?? new Date().toISOString().slice(0, 10),
    epics: epics.map(rowToEpic),
    stories: stories.map(rowToStory),
  };
}

export async function updateSprintConfig(updates: { projectName?: string; lastUpdated?: string }): Promise<boolean> {
  const payload: Record<string, unknown> = {};
  if (updates.projectName != null) payload.project_name = updates.projectName;
  if (updates.lastUpdated != null) payload.last_updated = updates.lastUpdated;
  if (Object.keys(payload).length === 0) return true;
  const { error } = await getSprintBoardAuthClient().from('sprint_board_config').upsert({ id: 'default', ...payload }, { onConflict: 'id' });
  return !error;
}

export async function updateStory(
  storyId: string,
  updates: {
    title?: string;
    assignee?: string;
    status?: string;
    epicId?: string | null;
    issueType?: 'story' | 'bug';
    userStory?: string;
    acceptanceCriteria?: string[];
    testCases?: string[];
  }
): Promise<boolean> {
  const payload: Record<string, unknown> = {};
  if (updates.title != null) payload.title = updates.title;
  if (updates.assignee != null) payload.assignee = updates.assignee;
  if (updates.status != null) payload.status = updates.status;
  if (updates.epicId !== undefined) payload.epic_id = updates.epicId;
  if (updates.issueType != null) payload.issue_type = updates.issueType;
  if (updates.userStory != null) payload.user_story = updates.userStory;
  if (updates.acceptanceCriteria != null) payload.acceptance_criteria = updates.acceptanceCriteria;
  if (updates.testCases != null) payload.test_cases = updates.testCases;
  if (Object.keys(payload).length === 0) return true;
  const { error } = await getSprintBoardAuthClient().from('sprint_board_stories').update(payload).eq('id', storyId);
  if (error) {
    console.warn('Sprint board updateStory error:', error);
    return false;
  }
  await updateSprintConfig({ lastUpdated: new Date().toISOString().slice(0, 10) });
  return true;
}

export async function updateEpic(epicId: string, updates: { title?: string; status?: string }): Promise<boolean> {
  const payload: Record<string, unknown> = {};
  if (updates.title != null) payload.title = updates.title;
  if (updates.status != null) payload.status = updates.status;
  if (Object.keys(payload).length === 0) return true;
  const { error } = await getSprintBoardAuthClient().from('sprint_board_epics').update(payload).eq('id', epicId);
  return !error;
}

/** Generate next numeric epic id from existing epic ids (e.g. "1".."10" -> "11"). */
function nextEpicId(epics: { id: string }[]): string {
  const nums = epics.map((e) => parseInt(e.id, 10)).filter((n) => !Number.isNaN(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
}

/** Generate next story id for an epic (e.g. "7.1", "7.2"). */
function nextStoryIdForEpic(stories: { id: string }[], epicId: string): string {
  const prefix = `${epicId}.`;
  const nums = stories
    .filter((s) => s.id.startsWith(prefix))
    .map((s) => parseInt(s.id.slice(prefix.length), 10))
    .filter((n) => !Number.isNaN(n));
  const n = nums.length ? Math.max(...nums) + 1 : 1;
  return `${epicId}.${n}`;
}

/** Generate short numeric id for story without epic (u1, u2, ...). */
function nextUncategorizedStoryId(uncategorizedStories: { id: string }[]): string {
  const nums = uncategorizedStories
    .map((s) => {
      const m = /^u(\d+)$/i.exec(s.id);
      return m ? parseInt(m[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `u${next}`;
}

export async function createEpic(
  existingEpics: { id: string }[],
  title: string
): Promise<{ id: string; title: string; status: string } | null> {
  const id = nextEpicId(existingEpics);
  const { error } = await getSprintBoardAuthClient().from('sprint_board_epics').insert({
    id,
    title: title.trim(),
    status: 'backlog',
  });
  if (error) {
    console.warn('createEpic error:', error);
    return null;
  }
  await updateSprintConfig({ lastUpdated: new Date().toISOString().slice(0, 10) });
  return { id, title: title.trim(), status: 'backlog' };
}

export async function createStory(
  payload: {
    epicId: string | null;
    title: string;
    assignee?: string;
    issueType?: 'story' | 'bug';
    userStory?: string;
    acceptanceCriteria?: string[];
    testCases?: string[];
  },
  existingStories: { id: string; epicId: string | null }[]
): Promise<SprintData['stories'][0] | null> {
  const title = payload.title?.trim();
  if (!title) return null;

  const id = payload.epicId
    ? nextStoryIdForEpic(
        existingStories.filter((s) => s.epicId === payload.epicId),
        payload.epicId
      )
    : nextUncategorizedStoryId(existingStories.filter((s) => s.epicId === null));

  const row = {
    id,
    epic_id: payload.epicId,
    title,
    assignee: payload.assignee ?? '',
    status: 'backlog',
    issue_type: payload.issueType ?? 'story',
    user_story: payload.userStory ?? null,
    acceptance_criteria: payload.acceptanceCriteria ?? [],
    test_cases: payload.testCases ?? [],
  };

  const { error } = await getSprintBoardAuthClient().from('sprint_board_stories').insert(row);
  if (error) {
    console.warn('createStory error:', error);
    return null;
  }
  await updateSprintConfig({ lastUpdated: new Date().toISOString().slice(0, 10) });

  return rowToStory(row as SprintBoardStoryRow);
}

export async function deleteStory(storyId: string): Promise<boolean> {
  const { error } = await getSprintBoardAuthClient().from('sprint_board_stories').delete().eq('id', storyId);
  if (error) {
    console.warn('deleteStory error:', error);
    return false;
  }
  await updateSprintConfig({ lastUpdated: new Date().toISOString().slice(0, 10) });
  return true;
}

/**
 * Subscribe to sprint board changes for real-time sync. Call onDataChange when config, epics, or stories change.
 */
export function subscribeSprintBoard(onDataChange: () => void): () => void {
  const channel = getSprintBoardAuthClient()
    .channel('sprint-board-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'sprint_board_config' }, () => onDataChange())
    .on('postgres_changes', { event: '*', schema: 'public', table: 'sprint_board_epics' }, () => onDataChange())
    .on('postgres_changes', { event: '*', schema: 'public', table: 'sprint_board_stories' }, () => onDataChange())
    .subscribe();

  return () => {
    if (channel?.unsubscribe) channel.unsubscribe();
  };
}
